/**
 * Shared port model + geometry.
 *
 * Ports are a physical interface that always lives on the *edge* of a parent
 * object (Equipment or Stocker). The same schema and geometry back both the
 * equipment overlay layer and the layout stocker elements, so there is a single
 * source of truth for how ports are positioned, constrained and created.
 *
 * Position is stored relative to the parent's axis-aligned bounding box as
 * fractions in [0, 1] (rx = 0 → left edge, rx = 1 → right edge). This keeps
 * ports attached correctly as the parent is moved, resized or rotated.
 *
 * New layouts seed four AMHS docks with independent functional `portKind` and
 * physical `bayType` fields. Those seeds are a starting point only — every port
 * can be renamed, retyped, repositioned or deleted from the Properties panel.
 */

import { getEquipmentLoadPortAnchors } from '../components/canvas/amhs/Equipment2D/equipmentGeometry';
import { generateId } from './geometry';
import { formatPortId } from './entityIds';

/** Directionality of a connection port. Becomes graph-edge semantics later. */
export type PortType = 'input' | 'output' | 'bidirectional';

/** Legacy bay values previously stored in `portKind`; kept readable. */
export type AmhsBayPortKind = 'Intrabay' | 'Interbay';

/** Functional tool dock kinds. */
export type LegacyEquipmentPortKind = 'LOAD' | 'UNLOAD';

/** Functional stocker rack kinds. */
export type LegacyStockerPortKind = 'UNLOAD_RAW' | 'UNLOAD_FINISHED';

/** AMHS port role for equipment (tools). */
export type EquipmentPortKind = AmhsBayPortKind | LegacyEquipmentPortKind;

/** AMHS port role for stockers. */
export type StockerPortKind = AmhsBayPortKind | LegacyStockerPortKind;

export type PortKind = EquipmentPortKind | StockerPortKind;

/** Physical bay classification, independent from functional port kind. */
export type BayType = 'interbay' | 'intrabay';

/** Stocker shelf column for live AMHS inventory (matches inventory.stockerColumns.kind). */
export type StockerColumnKind = 'raw' | 'processed';

/** How that column’s inventory is labeled (matches inventory.stockerColumns.inventoryLevel). */
export type StockerInventoryLevel = 'stocker' | 'bay';

export const BAY_TYPES: readonly BayType[] = ['interbay', 'intrabay'];
export const STOCKER_COLUMN_KINDS: readonly StockerColumnKind[] = ['raw', 'processed'];
export const STOCKER_INVENTORY_LEVELS: readonly StockerInventoryLevel[] = ['stocker', 'bay'];

/** Functional kinds offered for tool ports. */
export const EQUIPMENT_PORT_KINDS: readonly LegacyEquipmentPortKind[] = [
  'LOAD',
  'UNLOAD',
];
/** Functional kinds offered for stocker ports (legacy pick/drop; maps to columnKind). */
export const STOCKER_PORT_KINDS: readonly LegacyStockerPortKind[] = [
  'UNLOAD_RAW',
  'UNLOAD_FINISHED',
];
/** @deprecated Use {@link EQUIPMENT_PORT_KINDS}. */
export const LEGACY_EQUIPMENT_PORT_KINDS = EQUIPMENT_PORT_KINDS;
/** @deprecated Use {@link STOCKER_PORT_KINDS}. */
export const LEGACY_STOCKER_PORT_KINDS = STOCKER_PORT_KINDS;

/** AMHS rack role for legacy stocker load ports. */
export type StockerRackRole = 'picking' | 'dropping';

/** Dropdown label for a port kind. */
export function labelPortKind(kind: PortKind | string): string {
  const raw = String(kind).trim();
  const upper = raw.toUpperCase();
  if (upper === 'INTRABAY' || raw === 'Intrabay') return 'Intrabay';
  if (upper === 'INTERBAY' || raw === 'Interbay') return 'Interbay';
  if (upper === 'UNLOAD_RAW') return 'pick';
  if (upper === 'UNLOAD_FINISHED') return 'drop';
  return raw;
}

export function labelBayType(bayType: BayType | string): string {
  return String(bayType).toLowerCase() === 'interbay' ? 'Interbay' : 'Intrabay';
}

export function labelColumnKind(kind: StockerColumnKind | string): string {
  return String(kind).toLowerCase() === 'processed' ? 'Processed' : 'Raw';
}

export function labelInventoryLevel(level: StockerInventoryLevel | string): string {
  return String(level).toLowerCase() === 'bay' ? 'Bay' : 'Stocker';
}

/** Default inventoryLevel for a column (matches AMHS_RUN_INVENTORY.stockerColumns). */
export function defaultInventoryLevelForColumn(
  columnKind: StockerColumnKind
): StockerInventoryLevel {
  return columnKind === 'processed' ? 'bay' : 'stocker';
}

/** Map legacy stocker portKind → AMHS `rackRole`. */
export function stockerRackRole(
  portKind: PortKind | string | null | undefined
): StockerRackRole | undefined {
  const k = String(portKind ?? '')
    .trim()
    .toUpperCase();
  if (k === 'UNLOAD_RAW') return 'picking';
  if (k === 'UNLOAD_FINISHED') return 'dropping';
  return undefined;
}

/** Which edge of the parent bounding box a port is anchored to. */
export type PortEdge = 'top' | 'right' | 'bottom' | 'left';

/** What kind of object a port belongs to. */
export type PortParentType = 'equipment' | 'stocker';

/**
 * A connection port on an Equipment or Stocker. These become graph nodes for
 * AI-generated AMHS routing, so the identity (`id`), semantic role (`type`)
 * and outward `direction` are all first-class.
 *
 * `parentId` / `parentType` and `edge` are optional so that pre-existing
 * equipment ports (which were stored nested under their parent, without these
 * fields) continue to work without any data migration — they are populated
 * lazily as ports are created or edited.
 */
export type Port = {
  id: string;
  name: string;
  /**
   * Human-facing label for Properties UI and OHT From/To dropdowns.
   * Falls back to `name` when empty.
   */
  displayName?: string;
  /** Horizontal position as a fraction of the bbox width (0..1). */
  rx: number;
  /** Vertical position as a fraction of the bbox height (0..1). */
  ry: number;
  type: PortType;
  /** Functional AMHS role (`LOAD` / `UNLOAD` / `UNLOAD_*`). */
  portKind?: PortKind;
  /** Physical bay classification sent independently in AMHS payloads. */
  bayType?: BayType;
  /**
   * Stocker shelf column for live AMHS (`raw` / `processed`).
   * Must match a kind in `inventory.stockerColumns`.
   */
  columnKind?: StockerColumnKind;
  /**
   * How that column’s inventory is labeled (`stocker` / `bay`).
   * Should match the same column’s inventoryLevel in `inventory.stockerColumns`.
   */
  inventoryLevel?: StockerInventoryLevel;
  /** Outward-facing direction in degrees (0 = +x, 90 = +y), pre-rotation. */
  direction: number;
  /** Edge the port is anchored to (derived from rx/ry when absent). */
  edge?: PortEdge;
  /** Id of the owning Equipment instance or Stocker element. */
  parentId?: string;
  /** Whether the owner is an equipment overlay instance or a layout stocker. */
  parentType?: PortParentType;
  metadata?: Record<string, unknown>;
};

/** Minimum number of ports every Equipment / Stocker must retain. */
export const MIN_PORTS = 1;

/** Dock count seeded onto a brand-new equipment tool. */
export const DEFAULT_TOOL_PORT_COUNT = 2;
/** Dock count seeded onto a brand-new stocker. */
export const DEFAULT_STOCKER_PORT_COUNT = 4;
/** @deprecated Use the parent-specific default count. */
export const DEFAULT_PORT_COUNT = DEFAULT_STOCKER_PORT_COUNT;

/** Canvas colours per port directionality (legacy). */
export const PORT_COLORS: Record<PortType, string> = {
  input: '#22c55e',
  output: '#ef4444',
  bidirectional: '#3b82f6',
};

/** Canvas colours per AMHS port kind. */
export const PORT_KIND_COLORS: Record<PortKind, string> = {
  Intrabay: '#0ea5e9',
  Interbay: '#f59e0b',
  LOAD: '#22c55e',
  UNLOAD: '#ef4444',
  UNLOAD_RAW: '#f97316',
  UNLOAD_FINISHED: '#8b5cf6',
};

/** Normalize bay kind casing from mixed legacy / API strings. */
export function normalizeBayPortKind(
  kind: string | null | undefined
): AmhsBayPortKind | null {
  const upper = String(kind ?? '')
    .trim()
    .toUpperCase();
  if (upper === 'INTRABAY') return 'Intrabay';
  if (upper === 'INTERBAY') return 'Interbay';
  return null;
}

function portSlot(port: Pick<Port, 'id' | 'name'>): number | null {
  const match = `${port.name} ${port.id}`.match(/(?:LP|SL|PORT)[-_ ]?([1-4])\b/i);
  return match ? Number(match[1]) : null;
}

/** Resolve the physical bay, including layouts that stored it in `portKind`. */
export function resolveBayType(port: Port, parentType: PortParentType): BayType {
  // Tools are always intrabay — ignore stored / legacy interbay on equipment docks.
  if (parentType === 'equipment') return 'intrabay';
  if (port.bayType === 'interbay' || port.bayType === 'intrabay') return port.bayType;
  const legacyBay = normalizeBayPortKind(port.portKind);
  if (legacyBay) return legacyBay === 'Interbay' ? 'interbay' : 'intrabay';
  const slot = portSlot(port);
  return slot === 1 || slot === 3 ? 'interbay' : 'intrabay';
}

/** Resolve functional AMHS port kind, migrating bay-only legacy docks by slot. */
export function resolvePortKind(port: Port, parentType: PortParentType): PortKind {
  if (port.portKind && !normalizeBayPortKind(port.portKind)) {
    return port.portKind;
  }
  const slot = portSlot(port);
  if (parentType === 'equipment') {
    if (slot) return slot <= 2 ? 'LOAD' : 'UNLOAD';
    return port.type === 'output' ? 'UNLOAD' : 'LOAD';
  }
  if (port.columnKind === 'processed') return 'UNLOAD_FINISHED';
  if (port.columnKind === 'raw') return 'UNLOAD_RAW';
  if (slot) return slot <= 2 ? 'UNLOAD_RAW' : 'UNLOAD_FINISHED';
  return port.type === 'input' ? 'UNLOAD_FINISHED' : 'UNLOAD_RAW';
}

/** Resolve stocker column kind (raw / processed) for AMHS inventory tagging. */
export function resolveColumnKind(
  port: Port,
  parentType: PortParentType
): StockerColumnKind | undefined {
  if (parentType !== 'stocker') return undefined;
  if (port.columnKind === 'raw' || port.columnKind === 'processed') return port.columnKind;
  const kind = resolvePortKind(port, parentType);
  return kind === 'UNLOAD_FINISHED' ? 'processed' : 'raw';
}

/** Resolve stocker inventoryLevel for AMHS (defaults from columnKind). */
export function resolveInventoryLevel(
  port: Port,
  parentType: PortParentType
): StockerInventoryLevel | undefined {
  if (parentType !== 'stocker') return undefined;
  if (port.inventoryLevel === 'stocker' || port.inventoryLevel === 'bay') {
    return port.inventoryLevel;
  }
  const columnKind = resolveColumnKind(port, parentType);
  return columnKind ? defaultInventoryLevelForColumn(columnKind) : undefined;
}

/** Keep legacy UNLOAD_* portKind aligned when the user edits columnKind. */
export function portKindForColumnKind(columnKind: StockerColumnKind): LegacyStockerPortKind {
  return columnKind === 'processed' ? 'UNLOAD_FINISHED' : 'UNLOAD_RAW';
}

/** Canvas fill colour for a port marker. */
export function portColor(port: Port, parentType?: PortParentType): string {
  if (port.portKind) {
    const bay = normalizeBayPortKind(port.portKind);
    if (bay) return PORT_KIND_COLORS[bay];
    return PORT_KIND_COLORS[port.portKind] ?? PORT_COLORS[port.type];
  }
  if (parentType) return PORT_KIND_COLORS[resolvePortKind(port, parentType)];
  return PORT_COLORS[port.type];
}

/**
 * Next free 1-based slot for a new port on a parent. Ports can be deleted, so
 * the slot is derived from the highest `…-LP-N` already issued rather than from
 * the list length, which would otherwise reuse a live id.
 */
export function nextPortIndex(list: readonly Port[]): number {
  let max = 0;
  for (const port of list) {
    const match = String(port.id ?? '').match(/-LP-(\d+)$/i);
    const n = match ? Number(match[1]) : NaN;
    if (Number.isFinite(n) && n > max) max = n;
  }
  return Math.max(max, list.length) + 1;
}

/** Pick a port kind not yet used on the parent, or the first option. */
export function defaultPortKindToAdd(parentType: PortParentType, existing: Port[]): PortKind {
  const options = parentType === 'stocker' ? STOCKER_PORT_KINDS : EQUIPMENT_PORT_KINDS;
  const used = new Set(existing.map((p) => resolvePortKind(p, parentType)));
  return options.find((kind) => !used.has(kind)) ?? options[0];
}

/** A local, unrotated box a port lives on (parent-group coordinate space). */
export type PortBox = { x: number; y: number; w: number; h: number };

/** Outward-facing direction (degrees) for each edge. 0 = +x, 90 = +y. */
export function directionForEdge(edge: PortEdge): number {
  switch (edge) {
    case 'right':
      return 0;
    case 'bottom':
      return 90;
    case 'left':
      return 180;
    case 'top':
      return 270;
  }
}

/** Local position of a port on a box (before any parent rotation). */
export function portLocalPosition(port: Pick<Port, 'rx' | 'ry'>, box: PortBox) {
  return {
    x: box.x + port.rx * box.w,
    y: box.y + port.ry * box.h,
  };
}

/**
 * Project a local point onto the nearest edge of `box`, returning edge-snapped
 * relative coordinates plus the resolved edge and outward direction. Used to
 * constrain a port to the parent's perimeter while dragging (works for any box
 * size; parent rotation/scale is handled by the enclosing group, so callers
 * always pass points in the box's own unrotated space).
 */
export function projectPointToBoxEdge(
  localX: number,
  localY: number,
  box: PortBox
): { rx: number; ry: number; edge: PortEdge; direction: number } {
  const w = Math.max(1e-6, box.w);
  const h = Math.max(1e-6, box.h);
  let rx = Math.min(1, Math.max(0, (localX - box.x) / w));
  let ry = Math.min(1, Math.max(0, (localY - box.y) / h));

  const distLeft = rx;
  const distRight = 1 - rx;
  const distTop = ry;
  const distBottom = 1 - ry;
  const min = Math.min(distLeft, distRight, distTop, distBottom);

  let edge: PortEdge;
  if (min === distLeft) {
    rx = 0;
    edge = 'left';
  } else if (min === distRight) {
    rx = 1;
    edge = 'right';
  } else if (min === distTop) {
    ry = 0;
    edge = 'top';
  } else {
    ry = 1;
    edge = 'bottom';
  }

  return { rx, ry, edge, direction: directionForEdge(edge) };
}

/** Resolve a port's edge, falling back to inference from rx/ry when absent. */
export function portEdge(port: Port): PortEdge {
  if (port.edge) return port.edge;
  if (port.rx <= 0) return 'left';
  if (port.rx >= 1) return 'right';
  if (port.ry <= 0) return 'top';
  return 'bottom';
}

/** Build a port with sensible defaults; position defaults to bottom-centre. */
export function makePort(opts: {
  parentId?: string;
  parentType?: PortParentType;
  name?: string;
  displayName?: string;
  rx?: number;
  ry?: number;
  type?: PortType;
  portKind?: PortKind;
  bayType?: BayType;
  columnKind?: StockerColumnKind;
  inventoryLevel?: StockerInventoryLevel;
  edge?: PortEdge;
  /** Explicit port id; otherwise `{parentId}-LP-{portIndex}` or generateId(). */
  id?: string;
  /** 1-based index used with parentId to build typed port ids. */
  portIndex?: number;
}): Port {
  const edge: PortEdge = opts.edge ?? 'bottom';
  const rx = opts.rx ?? 0.5;
  const ry = opts.ry ?? 1;
  const parentType = opts.parentType;
  const portKind =
    opts.portKind ??
    (parentType === 'stocker'
      ? 'UNLOAD_RAW'
      : parentType === 'equipment'
        ? 'LOAD'
        : undefined);
  const bayType =
    opts.bayType ??
    (parentType === 'stocker'
      ? 'interbay'
      : parentType === 'equipment'
        ? 'intrabay'
        : undefined);
  const columnKind =
    opts.columnKind ??
    (parentType === 'stocker'
      ? portKind === 'UNLOAD_FINISHED'
        ? 'processed'
        : 'raw'
      : undefined);
  const inventoryLevel =
    opts.inventoryLevel ??
    (columnKind ? defaultInventoryLevelForColumn(columnKind) : undefined);
  const id =
    opts.id ??
    (opts.parentId && opts.portIndex != null
      ? formatPortId(opts.parentId, opts.portIndex)
      : generateId());
  const name = opts.name ?? 'Port 1';
  return {
    id,
    name,
    displayName: opts.displayName ?? name,
    rx,
    ry,
    type: opts.type ?? 'bidirectional',
    portKind,
    bayType,
    ...(columnKind ? { columnKind } : {}),
    ...(inventoryLevel ? { inventoryLevel } : {}),
    direction: directionForEdge(edge),
    edge,
    parentId: opts.parentId,
    parentType,
  };
}

/** Prefer displayName, then name. */
export function resolvePortDisplayName(port: Pick<Port, 'name' | 'displayName'>): string {
  const display = String(port.displayName ?? '').trim();
  if (display) return display;
  return String(port.name ?? '').trim() || 'Port';
}

/** Dropdown / list label: "Display Name (Name)". */
export function formatPortOptionLabel(port: Pick<Port, 'name' | 'displayName'>): string {
  const name = String(port.name ?? '').trim();
  const display = resolvePortDisplayName(port);
  if (name && display && name !== display) return `${display} (${name})`;
  return display || name || 'Port';
}

/** Default display name from parent + port kind (used when creating ports). */
export function defaultPortDisplayName(opts: {
  parentName?: string;
  toolType?: string;
  portKind?: PortKind | string;
  fallbackName?: string;
}): string {
  const kindRaw = String(opts.portKind ?? '').trim();
  const kind = kindRaw.toUpperCase();
  const type = String(opts.toolType ?? '').trim().toUpperCase();
  const parent = String(opts.parentName ?? '').trim();
  if (kind === 'INTRABAY' || kindRaw === 'Intrabay') {
    return parent ? `${parent} Intrabay` : type ? `${type} Intrabay` : 'Intrabay';
  }
  if (kind === 'INTERBAY' || kindRaw === 'Interbay') {
    return parent ? `${parent} Interbay` : type ? `${type} Interbay` : 'Interbay';
  }
  if (kind === 'UNLOAD_RAW') {
    return 'pickup port';
  }
  if (kind === 'UNLOAD_FINISHED') {
    return 'drop port';
  }
  if (kind === 'LOAD') {
    return parent ? `${parent} Load` : type ? `${type} Tool Load` : 'Tool Load';
  }
  if (kind === 'UNLOAD') {
    return parent ? `${parent} Unload` : type ? `${type} Tool Unload` : 'Tool Unload';
  }
  return opts.fallbackName || parent || 'Port';
}

/** Horizontal spacing for the four default stocker docks. */
const SYSTEM_PORT_RX = [0.2, 0.4, 0.6, 0.8] as const;
/** Standard two-port tool docks, centred on the equipment apron. */
const TOOL_PORT_RX = [0.45, 0.55] as const;

/**
 * Edge every stocker dock lives on. The OHT drop tray hangs off the stocker's
 * local top face, so all four docks share that face.
 */
export const STOCKER_PORT_EDGE: PortEdge = 'top';

/** Edge every equipment (tool) dock lives on. */
export const EQUIPMENT_PORT_EDGE: PortEdge = 'bottom';

/** @deprecated Prefer {@link STOCKER_PORT_EDGE} — all stocker docks share one edge. */
export const STOCKER_INTRABAY_PORT_EDGE: PortEdge = STOCKER_PORT_EDGE;
/** @deprecated Prefer {@link STOCKER_PORT_EDGE} — all stocker docks share one edge. */
export const STOCKER_INTERBAY_PORT_EDGE: PortEdge = STOCKER_PORT_EDGE;

const TOOL_PORT_KIND_BY_SLOT: readonly LegacyEquipmentPortKind[] = [
  'LOAD',
  'UNLOAD',
];

const STOCKER_PORT_KIND_BY_SLOT: readonly LegacyStockerPortKind[] = [
  'UNLOAD_RAW',
  'UNLOAD_RAW',
  'UNLOAD_FINISHED',
  'UNLOAD_FINISHED',
];

const STOCKER_BAY_TYPE_BY_SLOT: readonly BayType[] = [
  'interbay',
  'intrabay',
  'interbay',
  'intrabay',
];

/**
 * Seed ports for a new Equipment or Stocker on a single edge.
 * - Tools: one LOAD + one UNLOAD, all intrabay
 * - Stockers: two RAW + two FINISHED and two interbay + two intrabay
 */
export function makeDefaultPorts(opts: {
  parentId?: string;
  parentType: PortParentType;
  /** Required for precise equipment apron alignment (element footprint). */
  width?: number;
  height?: number;
}): Port[] {
  const isStocker = opts.parentType === 'stocker';
  const edge = isStocker ? STOCKER_PORT_EDGE : EQUIPMENT_PORT_EDGE;
  const ry = edge === 'top' ? 0 : 1;
  const kindCounts = new Map<string, number>();
  const portPositions = isStocker ? SYSTEM_PORT_RX : TOOL_PORT_RX;

  return portPositions.map((rx, index) => {
    const portKind = isStocker
      ? STOCKER_PORT_KIND_BY_SLOT[index]
      : TOOL_PORT_KIND_BY_SLOT[index];
    const bayType = isStocker ? STOCKER_BAY_TYPE_BY_SLOT[index] : 'intrabay';
    const columnKind: StockerColumnKind | undefined = isStocker
      ? portKind === 'UNLOAD_FINISHED'
        ? 'processed'
        : 'raw'
      : undefined;
    const kindCount = (kindCounts.get(portKind) ?? 0) + 1;
    kindCounts.set(portKind, kindCount);
    return makePort({
      ...opts,
      name: `${isStocker ? 'SL' : 'LP'}-${index + 1}`,
      displayName: `${labelPortKind(portKind)} ${kindCount}`,
      rx,
      ry,
      edge,
      portKind,
      bayType,
      ...(columnKind
        ? {
            columnKind,
            inventoryLevel: defaultInventoryLevelForColumn(columnKind),
          }
        : {}),
      portIndex: index + 1,
    });
  });
}

/**
 * Bring saved tool/stocker docks up to the current schema without overriding
 * anything the user chose: an empty list is seeded with the defaults, and every
 * other list keeps its own count, labels, kinds, bay types and placement while
 * missing fields are derived. Legacy docks that only ever stored a bay value in
 * `portKind` are migrated to the functional kind + `bayType` pair here.
 */
export function normalizePorts(
  parentId: string,
  parentType: PortParentType,
  ports: readonly Port[]
): Port[] {
  if (ports.length === 0) return makeDefaultPorts({ parentId, parentType });
  // The legacy tool default pair sits 0.05 either side of centre, far tighter
  // than a FOUP is wide. `buildAmhsRunRequest` already spaces it out before
  // sending it, so remapping here too keeps 2D markers, the 3D seats and the
  // backend's nodes describing one dock layout instead of three.
  const spaced =
    parentType === 'equipment'
      ? alignEquipmentPortsToLoadDocks([...ports], 1, 1)
      : ports;
  return spaced.map((port) => {
    const edge = portEdge(port);
    const portKind = resolvePortKind(port, parentType);
    const bayType = resolveBayType(port, parentType);
    const columnKind = resolveColumnKind(port, parentType);
    const inventoryLevel = resolveInventoryLevel(port, parentType);
    return {
      ...port,
      parentId,
      parentType,
      portKind,
      bayType,
      ...(columnKind ? { columnKind } : {}),
      ...(inventoryLevel ? { inventoryLevel } : {}),
      edge,
      direction: Number.isFinite(port.direction) ? port.direction : directionForEdge(edge),
    };
  });
}

/**
 * Move a stocker's load ports onto the tray edge. Layouts saved before the tray
 * side was settled put them on the opposite face, so the FOUP arrived on one
 * side and the ports sat on the other. Only the untouched legacy default pair
 * is moved — user-dragged ports are left alone.
 */
export function alignStockerPortsToTrayEdge(ports: Port[]): Port[] {
  const trayRy = STOCKER_PORT_EDGE === 'top' ? 0 : 1;
  const pairOffset = 0.05;

  const isUntouchedDefaultPair =
    ports.length === 2 &&
    ports.every(
      (p) =>
        portEdge(p) !== STOCKER_PORT_EDGE &&
        (p.ry <= 0 || p.ry >= 1) &&
        Math.abs(Math.abs(p.rx - 0.5) - pairOffset) < 1e-6
    );

  if (!isUntouchedDefaultPair) return ports;

  return ports.map((p) => ({
    ...p,
    ry: trayRy,
    edge: STOCKER_PORT_EDGE,
    direction: directionForEdge(STOCKER_PORT_EDGE),
  }));
}

/** Horizontal offset (fraction of bbox width) of the legacy two-port default pair. */
const DEFAULT_PORT_PAIR_RX_OFFSET = 0.05;

/**
 * Move equipment load ports onto the EFEM FOUP pedestals (apron), matching the
 * 2D Equipment2D docks. Only remaps the untouched legacy bottom-edge default
 * pair — once a port has been dragged, its position is left alone.
 */
export function alignEquipmentPortsToLoadDocks(
  ports: Port[],
  width: number,
  height: number
): Port[] {
  if (!(width > 0 && height > 0) || ports.length === 0) return ports;

  const isLegacyBottomDefaultPair =
    ports.length === 2 &&
    ports.every(
      (p) =>
        p.ry >= 0.98 &&
        Math.abs(Math.abs(p.rx - 0.5) - DEFAULT_PORT_PAIR_RX_OFFSET) < 1e-3
    );

  if (!isLegacyBottomDefaultPair) return ports;

  const anchors = getEquipmentLoadPortAnchors(width, height, ports.length);
  return ports.map((p, i) => {
    const a = anchors[i] ?? anchors[anchors.length - 1];
    if (!a) return p;
    return {
      ...p,
      rx: a.rx,
      ry: a.ry,
      edge: EQUIPMENT_PORT_EDGE,
      direction: directionForEdge(EQUIPMENT_PORT_EDGE),
    };
  });
}

/** @deprecated Prefer {@link makeDefaultPorts}; returns the first default port. */
export function makeDefaultPort(opts: {
  parentId?: string;
  parentType?: PortParentType;
  name?: string;
}): Port {
  const parentType = opts.parentType ?? 'equipment';
  return makeDefaultPorts({ ...opts, parentType })[0];
}

/** Append a port to a list (returns a new array). */
export function addPortToList(list: Port[], port: Port): Port[] {
  return [...list, port];
}

/** Patch a single port in a list by id (returns a new array). */
export function updatePortInList(list: Port[], id: string, changes: Partial<Port>): Port[] {
  return list.map((p) => (p.id === id ? { ...p, ...changes } : p));
}

/**
 * Remove a port by id — but never below {@link MIN_PORTS}. Returns the list
 * unchanged when removal is refused.
 */
export function removePortFromList(list: Port[], id: string): Port[] {
  if (list.length <= MIN_PORTS) return list;
  return list.filter((p) => p.id !== id);
}

/** Whether a port can be deleted given the current list. */
export function canRemovePort(list: readonly Port[]): boolean {
  return list.length > MIN_PORTS;
}
